import { useState } from "react";

import { Box, Chip, Stack, Tab, Tabs, Typography } from "@mui/material";

import { TIER_LABEL_BY_TIER } from "@aclymatepackages/constants";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey } from "@fortawesome/free-solid-svg-icons";

import Card from "../atoms/mui/Card";
import GrayPaper from "../atoms/GrayPaper";
import CopyToClipboardIcon from "../atoms/buttons/CopyToClipboardIcon";
import Link from "../atoms/mui/Link";

import { analyticsTrack } from "../../helpers/analytics";

const MCP_BASE_URL = "https://mcp.aclymate.com/mcp";

const BetaChip = () => (
  <Chip label="Beta" size="small" color="warning" variant="outlined" />
);

const EngineTabLabel = ({ label, beta }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <span>{label}</span>
    {beta && <BetaChip />}
  </Stack>
);

const CopyableRow = ({ label, value, component = "code", multiline }) => (
  <GrayPaper style={{ padding: 12 }}>
    <Stack spacing={0.5}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Stack
        direction="row"
        alignItems={multiline ? "flex-start" : "center"}
        justifyContent="space-between"
        spacing={1}
      >
        <Box
          component={component}
          sx={{
            fontFamily: "monospace",
            fontSize: multiline ? 12 : undefined,
            whiteSpace: multiline ? "pre-wrap" : undefined,
            wordBreak: "break-word",
            m: 0
          }}
        >
          {value}
        </Box>
        <CopyToClipboardIcon text={value} />
      </Stack>
    </Stack>
  </GrayPaper>
);

const BaseUrlRow = () => <CopyableRow label="Base URL" value={MCP_BASE_URL} />;

const TryItLine = () => (
  <Typography variant="body2" color="textSecondary">
    Try asking: &quot;What&apos;s my company&apos;s Scope 2 emissions this year?&quot;
  </Typography>
);

const BetaCaption = ({ children }) => (
  <Typography variant="caption" color="textSecondary">
    {children}
  </Typography>
);

const CLAUDE_CONFIG_SNIPPET = `{
  "mcpServers": {
    "aclymate": {
      "command": "npx",
      "args": [
        "mcp-remote",
        "${MCP_BASE_URL}",
        "--header",
        "Authorization: Bearer <your-key>"
      ]
    }
  }
}`;

const ClaudeInstructions = () => (
  <Stack spacing={2}>
    <BetaCaption>
      This exact prompt-for-a-key step is still being verified — if Claude adds
      the connector without asking for your key, use the config-file method
      below instead.
    </BetaCaption>
    <Typography variant="subtitle2">Recommended: Claude Connectors</Typography>
    <Typography variant="body2" color="textSecondary">
      1. In Claude, go to Settings → Connectors → <strong>+ Add</strong> →{" "}
      <strong>Custom</strong>.
      <br />
      2. Enter a Name — e.g. &quot;Aclymate&quot; — and paste the Remote MCP server URL
      below.
      <br />
      3. Leave &quot;OAuth Client ID&quot; / &quot;OAuth Client Secret&quot; blank — Aclymate&apos;s MCP
      server authenticates with a bearer-token header, not OAuth.
      <br />
      4. Choose &quot;Individual sign-in&quot; (each teammate connects with their own key)
      unless your organization wants &quot;Managed authorization&quot; instead.
      <br />
      5. Click <strong>Add</strong>. If Claude prompts you to authenticate,
      enter your Aclymate API key when asked.
    </Typography>
    <BaseUrlRow />
    <Typography variant="subtitle2">
      Fallback: Claude Desktop config file
    </Typography>
    <Typography variant="body2" color="textSecondary">
      If the native flow above doesn&apos;t accept your key, add this block to your{" "}
      <code>claude_desktop_config.json</code> and restart Claude Desktop
      instead.
    </Typography>
    <CopyableRow
      label="claude_desktop_config.json"
      value={CLAUDE_CONFIG_SNIPPET}
      component="pre"
      multiline
    />
    <Typography variant="caption" color="textSecondary">
      Replace &lt;your-key&gt; with your Aclymate API key above.
    </Typography>
    <TryItLine />
  </Stack>
);

const ChatGptInstructions = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="textSecondary">
      1. Open your Aclymate Custom GPT (or create one) in ChatGPT&apos;s GPT Builder.
      <br />
      2. Go to Configure → Actions → Authentication.
      <br />
      3. Choose &quot;API Key,&quot; Auth Type &quot;Custom,&quot; header name{" "}
      <code>Authorization</code>.
      <br />
      4. Paste <code>Bearer &lt;your-key&gt;</code> as the value.
    </Typography>
    <BaseUrlRow />
    <CopyableRow label="Header value" value="Bearer <your-key>" />
    <TryItLine />
  </Stack>
);

const GeminiInstructions = () => (
  <Stack spacing={2}>
    <BetaCaption>
      Gemini&apos;s static-credential fallback path is still being validated against
      a live account — if these steps don&apos;t match what you see,{" "}
      <Link
        href="mailto:support@aclymate.com"
        color="inherit"
        underline="always"
      >
        contact support
      </Link>
      .
    </BetaCaption>
    <Typography variant="body2" color="textSecondary">
      1. In the Gemini app, go to Settings → Connected Apps → Custom Apps.
      <br />
      2. Choose &quot;Add a custom app&quot; and paste the base URL below.
      <br />
      3. If prompted for Dynamic Client Registration and it doesn&apos;t complete,
      open &quot;Advanced features → credentials&quot; and enter your Aclymate key as a
      static credential instead.
    </Typography>
    <BaseUrlRow />
    <CopyableRow
      label="Authorization header"
      value="Authorization: Bearer <your-key>"
    />
    <TryItLine />
  </Stack>
);

const ConnectionInstructionsTabs = ({
  tier,
  apiKeyMasked,
  analyticsEventPrefix = "api_key"
}) => {
  const [activeEngine, setActiveEngine] = useState("claude");
  const tierLabel = TIER_LABEL_BY_TIER[tier] || tier;

  const onChangeEngine = (_, nextEngine) => {
    setActiveEngine(nextEngine);
    analyticsTrack(`${analyticsEventPrefix}_connection_tab_switched`, {
      tier,
      engine: nextEngine
    });
  };

  return (
    <Card
      title="Connect your agent"
      subtitle={`Point Claude, ChatGPT, or Gemini at Aclymate's ${tierLabel} MCP surface.`}
      icon={<FontAwesomeIcon icon={faKey} />}
    >
      <Box px={2} pb={2}>
        <Stack spacing={2}>
          {apiKeyMasked && (
            <Typography variant="caption" color="textSecondary">
              These steps apply to your active key (<code>{apiKeyMasked}</code>
              ). If you have more than one active key, use the one you intend to
              connect.
            </Typography>
          )}
          <Tabs value={activeEngine} onChange={onChangeEngine}>
            <Tab
              value="claude"
              label={<EngineTabLabel label="Claude" beta />}
            />
            <Tab value="chatgpt" label="ChatGPT" />
            <Tab
              value="gemini"
              label={<EngineTabLabel label="Gemini" beta />}
            />
          </Tabs>
          {activeEngine === "claude" && <ClaudeInstructions />}
          {activeEngine === "chatgpt" && <ChatGptInstructions />}
          {activeEngine === "gemini" && <GeminiInstructions />}
        </Stack>
      </Box>
    </Card>
  );
};

export default ConnectionInstructionsTabs;
